// What the customer does in Miro before a team can be checked (spec section
// 3): a connector user in the team, a private Miro app with the scopes the
// connector needs, its token in the team's vault, and the connector user as
// co-owner of each board people should be able to request. The team's setup
// page shows these steps above the Terraform that lets the connector read the
// token's secret.
//
// The shape follows LaunchDarkly's
// (packages/integrations/launchdarkly/frontend/src/preamble.tsx).
import { MiroAiComponents } from "@p0security/integrations-miro-ai-shared/components";
import type { MiroAiTeamConfig } from "@p0security/integrations-miro-ai-shared/types";
import { Typography } from "@p0security/ui";

const { Paragraph, Text } = Typography;

type VaultKind = MiroAiTeamConfig["vault"]["type"];

const { vault: vaultElement } = MiroAiComponents.team.schema;

/** A vault, as the team's vault field names it. */
export const vaultLabel = (vault: VaultKind) =>
  vaultElement.options[vault].label;

/** The secret, as the setup page names it: by its name, or as the team's own
 * when a stored item has none. */
const secretOf = (secretName: unknown) =>
  typeof secretName === "string" ? (
    <Text code>{secretName}</Text>
  ) : (
    "this team names"
  );

/**
 * The Miro steps for a team, which the team's setup page shows above its
 * read grant's Terraform, and what to know before relying on it.
 */
export const toTokenSteps = (args: {
  secretName: unknown;
  vault: VaultKind;
}) => {
  const { secretName, vault } = args;
  return (
    <>
      <Paragraph>If you haven&apos;t yet, in Miro:</Paragraph>
      <ol>
        <li>
          Create a Miro user for P0&apos;s connector, and add it to the team. On
          a free plan it takes one of the team&apos;s seats.
        </li>
        <li>
          If the team restricts which apps can be installed, have a Team Admin
          approve the app below.
        </li>
        <li>
          Signed in as the connector user, create a private Miro app with the
          scopes <Text code>boards:read</Text>, <Text code>boards:write</Text>{" "}
          and <Text code>team:read</Text>, and leave &quot;Expire user
          authorization token&quot; unchecked. Click &quot;Install app and get
          OAuth token&quot;, and store the token in {vaultLabel(vault)} as the
          secret {secretOf(secretName)}, holding the token alone. P0 never sees
          it.
        </li>
        <li>
          Make the connector user co-owner of each board people should be able
          to request. P0 can share only those boards.
        </li>
      </ol>
      <Paragraph>
        A free team holds 3 boards, and Miro shows every share and role change
        as made by the connector user. Then apply the Terraform below so that
        the connector can read the secret.
      </Paragraph>
    </>
  );
};
